import React, { useContext, useEffect, useRef, useState } from 'react';
import Paragraph from '../../../atoms/paragraphs/Paragraph';
import CheckoutPageOne from './CheckoutPageOne';
import CheckoutPageTwo from './CheckoutPageTwo';
import CheckoutPagePaymentResponse from './CheckoutPagePaymentResponse';
import Image from '../../../atoms/images/Image';
import { useLocation } from 'react-router-dom';
import { CheckoutService } from '../../../../services/CheckoutService';
import { AppContext } from '../../../../contexts/AppContext';
import { useHistory } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import { buildPath } from '../../../organisms/containers/util/pathHelper';
import Bugsnag from '@bugsnag/js';

export const defaultSubscriptionCycles = [
  {
    id: 'month',
    discount: null,
    disabled: false,
  },
  {
    id: 'semester',
    discount: 5,
    disabled: false,
  },
  {
    id: 'year',
    discount: 16,
    disabled: false,
  },
  {
    id: 'biennial',
    discount: 25,
    disabled: false,
  },
];

export const defaultPaymentMethods = [
  {
    id: 'credit_card',
    sibs_id: 'CARD',
    payment_id: 'CC',
    label: ['Cartão de crédito'],
    description: 'paymentMethod_credit_card_description',
    icons: [
      'https://invoicexpress-assets.s3.eu-west-1.amazonaws.com/visa_master_logo.png',
    ],
    disabled: false,
    disabled_cycles: [],
  },
  {
    id: 'mbway',
    sibs_id: 'MBWAY',
    payment_id: 'MBW',
    label: [
      'MB WAY',
      'https://invoicexpress-assets.s3.eu-west-1.amazonaws.com/mb_way_logo.png',
    ],
    description: 'paymentMethod_mbway_description',
    icons: [],
    disabled: false,
    disabled_cycles: ['month'],
  },
  {
    id: 'multibanco',
    sibs_id: 'REFERENCE',
    payment_id: 'MB',
    label: [
      'Referência Multibanco',
      'https://invoicexpress-assets.s3.eu-west-1.amazonaws.com/mb_logo.png',
    ],
    description: 'paymentMethod_multibanco_description',
    icons: [],
    disabled: false,
    disabled_cycles: ['month'],
  },
];
/**
 * CheckoutLayout component with green gradient background and white centered card
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Content to be rendered inside the white card
 * @returns {React.ReactElement} CheckoutLayout component
 */
const CheckoutLayout = ({ intl, ...props }) => {
  const { appState } = useContext(AppContext);
  const history = useHistory();
  const location = useLocation();
  const containerRef = useRef(null);
  const [selectedCycle, setSelectedCycle] = useState('year');
  const [currentCycle, setCurrentCycle] = useState(undefined);
  const [subscriptionCycles, setSubscriptionCycles] = useState(
    defaultSubscriptionCycles
  );
  const [selectedMethod, setSelectedMethod] = useState(
    defaultPaymentMethods.find((method) => method.disabled === false)
  );
  const [currentPage, setCurrentPage] = useState('one');

  const [plans, setPlans] = useState([]);
  const [plansFeatures, setPlansFeatures] = useState([]);

  const [selectedCardPlanIndex, setSelectedCardPlanIndex] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState({});
  const [leftActionDisabled, setLeftActionDisabled] = useState(false);
  const [rightActionDisabled, setRightActionDisabled] = useState(false);

  const [orderSummary, setOrderSummary] = useState(undefined);
  const [isReferencePayment, setIsReferencePayment] = useState(undefined);
  const [isFreePlan, setIsFreePlan] = useState(undefined);
  const [transactionID, setTransactionID] = useState(undefined);
  const [checkoutInfo, setCheckoutInfo] = useState(undefined);

  const [coupon, setCoupon] = useState(undefined);
  const [couponError, setCouponError] = useState(false);
  const [couponSuccess, setCouponSuccess] = useState(false);

  const [paymentMethods, setPaymentMethods] = useState(defaultPaymentMethods);
  const [paymentMethod, setPaymentMethod] = useState(undefined);
  const [forceNewPaymentMethod, setForceNewPaymentMethod] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [preconditionError, setPreconditionError] = useState(false);

  const [isUpgrade, setIsUpgrade] = useState(false);
  const [isDowngrade, setIsDowngrade] = useState(false);
  const [isCycleChange, setIsCycleChange] = useState(false);

  const handleCycleChange = (cycleId) => {
    setSelectedCycle(cycleId);
  };

  const handlePaymentMethodChange = (method) => {
    if (!method.disabled && !method.disabled_cycles?.includes(selectedCycle)) {
      setSelectedMethod(method);
    }
  };

  const applyCoupon = async (coupon) => {
    const response = await CheckoutService.submitOrderSummary(
      selectedPlan.id,
      selectedCycle,
      coupon
    );

    if (!response.error) {
      if (response.summary.discount_coupon === 0) {
        setCouponError(true);
        setCouponSuccess(false);

        setTimeout(() => {
          setCouponError(false);
        }, 5000);
      } else {
        setCouponError(false);
        setCouponSuccess(true);

        setTimeout(() => {
          setCouponSuccess(false);
        }, 5000);
      }

      setOrderSummary(response.summary);
    } else {
      Bugsnag.notify(new Error(response.error));
    }
  };

  const advance = async () => {
    if (selectedPlan.id === 87) {
      const response = await CheckoutService.getFreePlanSetup(
        selectedPlan.id,
        selectedCycle
      );

      if (!response.error) {
        setIsFreePlan(true);
        history.push({
          pathname: '',
          search:
            '?page=paymentResponse&isFreePlan=true&cycle=' +
            currentCycle +
            '&selectedCycle=' +
            selectedCycle +
            '&currentPlan=87' +
            '&selectedPlan=87',
        });
      }
    } else if (
      appState.accountTrial === 'false' &&
      appState.subscriptionExpired === 'false' &&
      currentCycle === undefined
    ) {
      window.location.href = buildPath('accounts/details');
    } else {
      const response = await CheckoutService.submitOrderSummary(
        selectedPlan.id,
        selectedCycle
      );

      if (!response.error) {
        if (response.status === 412) {
          Bugsnag.notify(new Error(response.summary));
          setPreconditionError(true);
        } else {
          setOrderSummary(response.summary);
          setPreconditionError(false);
        }
        setIsLoading(false);
        history.push({
          pathname: '',
          search:
            '?page=two&cycle=' +
              currentCycle +
              '&selectedCycle=' +
              selectedCycle +
              '&currentPlan=' +
              plans.find((plan) => plan.current_plan === true)?.id ||
            undefined + '&selectedPlan=' + selectedPlan.id,
        });
      } else {
        Bugsnag.notify(new Error(response.error));
      }
    }
  };

  useEffect(() => {
    if (plans.length === 0) return;

    if (
      selectedCardPlanIndex !== 0 &&
      selectedCardPlanIndex !== plans.length - 1
    ) {
      if (
        plans[selectedCardPlanIndex - 1].disabled ||
        plans[selectedCardPlanIndex - 1][selectedCycle + '_disabled']
      ) {
        setLeftActionDisabled(true);
      } else {
        setLeftActionDisabled(false);
      }

      if (
        plans[selectedCardPlanIndex + 1].disabled ||
        plans[selectedCardPlanIndex + 1][selectedCycle + '_disabled']
      ) {
        setRightActionDisabled(true);
      } else {
        setRightActionDisabled(false);
      }
    } else if (selectedCardPlanIndex === 0) {
      setLeftActionDisabled(true);
    } else if (selectedCardPlanIndex === plans.length - 1) {
      setRightActionDisabled(true);
    }

    setSelectedPlan(plans[selectedCardPlanIndex]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans, selectedCardPlanIndex, selectedCycle]);

  useEffect(() => {
    const fetchPaymentMethod = async () => {
      const response = await CheckoutService.getPaymentMethod();

      if (!response.error && response.method) {
        const apiType = response.method.type;
        if (apiType === 'DD') {
          setForceNewPaymentMethod(true);
          setPaymentMethods(defaultPaymentMethods.map((pm) => ({ ...pm })));
          return;
        }
        setForceNewPaymentMethod(false);
        setPaymentMethod(response.method);
        const matchingMethod = defaultPaymentMethods.find(
          (method) => method.payment_id === apiType
        );
        if (matchingMethod) {
          setSelectedMethod(matchingMethod);
        }
        setPaymentMethods(
          defaultPaymentMethods.map((pm) => ({
            ...pm,
            disabled: pm.payment_id !== apiType,
          }))
        );
      } else if (response.error) {
        Bugsnag.notify(new Error(response.error));
      }
    };

    if (location?.search) {
      const searchParams = new URLSearchParams(location.search);
      const page = searchParams.get('page');
      const selectedPlanId = searchParams.get('selectedPlan');
      const currentPlanId = searchParams.get('currentPlan');
      const cycle = searchParams.get('cycle');
      const selectedCycle = searchParams.get('selectedCycle');
      const transactionID = searchParams.get('transactionID');
      const checkoutInfo = searchParams.get('checkoutInfo');
      const coupon = searchParams.get('coupon');
      const isReferencePayment = searchParams.get('isReferencePayment');
      const isFreePlan = searchParams.get('isFreePlan');

      setCurrentPage(page || 'one');
      setCurrentCycle(cycle || undefined);

      if (cycle === 'biennial') {
        setSelectedCycle('biennial');
      }

      if (selectedCycle) {
        setSelectedCycle(selectedCycle);
      }

      setTransactionID(transactionID || undefined);
      setCheckoutInfo(JSON.parse(checkoutInfo) || undefined);
      setCoupon(coupon || undefined);
      setIsReferencePayment(isReferencePayment || undefined);
      setIsFreePlan(isFreePlan || undefined);

      if (cycle && plans.length > 0) {
        const cycleIndex = subscriptionCycles.findIndex(
          (item) => item.id === cycle
        );

        if (cycleIndex !== -1) {
          let currentPlan = plans.find((plan) => plan.current_plan === true);

          if (currentPlan && currentPlan.id === 87) {
            setSubscriptionCycles(defaultSubscriptionCycles);
          } else {
            let updatedSubscriptionCycles = subscriptionCycles;
            updatedSubscriptionCycles.forEach((item, index) => {
              if (
                appState.accountTrial === 'false' &&
                appState.subscriptionExpired === 'false' &&
                appState.inRenewalWindow === 'false' &&
                index < cycleIndex
              ) {
                item.disabled = true;
              }
            });

            setSubscriptionCycles(updatedSubscriptionCycles);
          }
        }
      }

      if (selectedPlanId && plans.length > 0) {
        const paramsSelectedPlan = plans.find(
          (plan) => plan.id === parseInt(selectedPlanId)
        );

        if (paramsSelectedPlan) {
          setSelectedPlan(paramsSelectedPlan);
        }
      }

      if (
        page === 'two' &&
        appState.accountTrial === 'false' &&
        appState.subscriptionExpired === 'false' &&
        paymentMethod === undefined
      ) {
        fetchPaymentMethod();
      }

      if (page === 'paymentResponse') {
        let checkCurrentPlanId = currentPlanId;

        if (checkCurrentPlanId === 'undefined' || checkCurrentPlanId === null) {
          checkCurrentPlanId = 0;
        } else {
          checkCurrentPlanId = parseInt(currentPlanId);
        }

        let checkUpgrade = parseInt(selectedPlanId) > checkCurrentPlanId;
        let checkDowngrade = parseInt(selectedPlanId) < checkCurrentPlanId;
        let checkCycle = selectedCycle !== cycle;

        setIsUpgrade(checkUpgrade);
        setIsDowngrade(checkDowngrade);
        setIsCycleChange(checkCycle);
      }
    }

    containerRef.current?.scrollTo({
      top: 0,
      left: 0,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, plans]);

  useEffect(() => {
    const fetchPlans = async () => {
      const response = await CheckoutService.getPlansList();

      if (!response.error) {
        let currentPlan = response.plans.findIndex(
          (plan) => plan.id === parseInt(appState.planId)
        );

        let recommendedPlan = response.plans.findIndex(
          (plan) => plan.recommended === true
        );

        if (
          currentPlan === -1 ||
          appState.accountTrial === 'true' ||
          appState.subscriptionExpired === 'true'
        ) {
          currentPlan = 0;
        }

        const responsePlans = response.plans.map((plan, index) => {
          return {
            ...plan,
            current_plan:
              appState.accountTrial === 'false' &&
              appState.subscriptionExpired === 'false' &&
              plan.id === parseInt(appState.planId),
            features: plan.features.map((feature) => {
              return feature.id ? feature.feature_id : undefined;
            }),
            disabled:
              (currentPlan !== -1 &&
                index < currentPlan &&
                appState.inRenewalWindow === 'false') ||
              plan.disabled,
            month_disabled: plan.id === 87 || plan.id === 88,
            semester_disabled: plan.id === 87 || plan.id === 88,
            year_disabled: false,
            biennial_disabled: false,
          };
        });

        setPlans(responsePlans);

        const responseSelectedPlanIndex = responsePlans.findIndex(
          (plan) => plan.id === parseInt(appState.planId)
        );

        if (
          responseSelectedPlanIndex !== -1 &&
          appState.accountTrial === 'false' &&
          appState.subscriptionExpired === 'false'
        ) {
          setSelectedCardPlanIndex(responseSelectedPlanIndex);
        } else {
          setSelectedCardPlanIndex(
            recommendedPlan === -1 ? 0 : recommendedPlan
          );
        }
      } else {
        Bugsnag.notify(new Error(response.error));
      }
    };

    const fetchFeatures = async () => {
      const response = await CheckoutService.getPlansFeatures();

      if (!response.error) {
        setPlansFeatures(response.features);
      } else {
        Bugsnag.notify(new Error(response.error));
      }
    };

    if (
      new URLSearchParams(location?.search).get('page') !== 'paymentResponse'
    ) {
      fetchPlans();
      fetchFeatures();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className='d-flex flex-column checkout-layout'>
      <div className='d-flex flex-column w-100 checkout-layout__wrapper'>
        <div className='d-flex flex-column h-100 w-100 align-items-center'>
          <div className='checkout-layout__title px-5 mb-4'>
            <Paragraph className='text-header h4 text-center'>
              {
                intl.messages[
                  `checkoutTitle${isUpgrade ? 'Upgrade' : ''}${isDowngrade ? 'Downgrade' : ''}${isCycleChange ? 'Cycle' : ''}`
                ]
              }
            </Paragraph>
          </div>

          <div
            className={`d-flex flex-column checkout-layout__container col-12 col-lg-10 col-xxl-8 px-0 overflow-hidden position-relative`}
          >
            <div
              ref={containerRef}
              className='d-flex flex-column h-100 overflow-auto py-4 px-4 px-md-5'
            >
              <div className='d-flex flex-row flex-grow-1 justify-content-center align-items-center w-100'>
                <div className='d-flex flex-column h-100 w-100 align-items-center'>
                  <Image
                    src='https://invoicexpress-assets.s3.eu-west-1.amazonaws.com/ix-logo-horiz.svg'
                    alt='InvoiceXpress Logo'
                    className='logo mt-2 mb-4'
                  />
                  {currentPage === 'one' && (
                    <CheckoutPageOne
                      subscriptionCycles={subscriptionCycles}
                      selectedCycle={selectedCycle}
                      currentCycle={currentCycle}
                      handleCycleChange={handleCycleChange}
                      plans={plans}
                      selectedPlan={selectedPlan}
                      setSelectedCardPlanIndex={setSelectedCardPlanIndex}
                      leftActionDisabled={leftActionDisabled}
                      rightActionDisabled={rightActionDisabled}
                      plansFeatures={plansFeatures}
                      advance={advance}
                      inRenewalWindow={appState.inRenewalWindow}
                    ></CheckoutPageOne>
                  )}
                  {currentPage === 'two' && (
                    <CheckoutPageTwo
                      paymentMethods={paymentMethods}
                      selectedMethod={selectedMethod}
                      handlePaymentMethodChange={handlePaymentMethodChange}
                      plans={plans}
                      plansFeatures={plansFeatures}
                      currentCycle={currentCycle}
                      selectedCycle={selectedCycle}
                      currentPlan={
                        appState.accountTrial === 'false' &&
                        appState.subscriptionExpired === 'false'
                          ? plans.find((plan) => plan.current_plan === true)
                          : undefined
                      }
                      selectedPlan={selectedPlan}
                      orderSummary={orderSummary}
                      coupon={coupon}
                      applyCoupon={applyCoupon}
                      couponError={couponError}
                      couponSuccess={couponSuccess}
                      paymentMethod={paymentMethod}
                      forceNewPaymentMethod={forceNewPaymentMethod}
                      fromParentIsLoading={isLoading}
                      preconditionError={preconditionError}
                      setPreconditionError={setPreconditionError}
                    ></CheckoutPageTwo>
                  )}
                  {currentPage === 'paymentResponse' && (
                    <CheckoutPagePaymentResponse
                      transactionID={transactionID}
                      checkoutInfo={checkoutInfo}
                      isReferencePayment={isReferencePayment}
                      isFreePlan={isFreePlan}
                      isUpgrade={isUpgrade}
                      isDowngrade={isDowngrade}
                      cycleChange={isCycleChange}
                    ></CheckoutPagePaymentResponse>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default injectIntl(CheckoutLayout);
